import type { Condition, ExperimentState, RevealEvent, Story } from "../types";

export const CONDITION_SEQUENCES: Condition[][] = [
  ["L", "P", "H"],
  ["L", "H", "P"],
  ["P", "L", "H"],
  ["P", "H", "L"],
  ["H", "L", "P"],
  ["H", "P", "L"],
];

export const CONDITION_TEST_LABELS: Record<Condition, string> = {
  L: "L（基本表示）",
  P: "P（主要情報）",
  H: "H（全情報オン）",
};

export function makeId(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`;
}

export function createInitialState(guideVisible = true): ExperimentState {
  const sequence = CONDITION_SEQUENCES[
    Math.floor(Math.random() * CONDITION_SEQUENCES.length)
  ];

  return {
    version: 1,
    sessionId: makeId("session"),
    participantCode: `TEST-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    stage: "pre-questionnaire",
    blockNumber: 0,
    sequence,
    guideVisible,
    furthestReadIndex: 0,
    currentViewportIndex: 0,
    activeTab: "text",
    preAnswers: {},
    trials: [],
    finalAnswers: {},
  };
}

export function visibleEvents(story: Story, furthestReadIndex: number) {
  return story.events
    .filter((event) => event.at <= furthestReadIndex)
    .sort((a, b) => a.at - b.at);
}

export function buildGraphState(story: Story, furthestReadIndex: number) {
  const people: Record<string, Record<string, string>> = {};
  const relations: Record<string, Record<string, string | boolean>> = {};

  for (const event of visibleEvents(story, furthestReadIndex)) {
    if (event.type === "person") {
      people[event.target] = { ...(people[event.target] ?? {}), ...event.value };
    } else {
      relations[event.target] = {
        ...(relations[event.target] ?? {}),
        ...event.value,
      };
    }
  }

  return { people, relations };
}

export function detailFieldsForCondition(
  condition: Condition,
  details: Record<string, string | boolean>,
) {
  if (condition === "L") return {};
  const preferred = ["role", "goal", "status"];
  if (condition === "P") {
    return Object.fromEntries(
      Object.entries(details).filter(([key]) => preferred.includes(key)),
    );
  }
  return Object.fromEntries(
    Object.entries(details).filter(([key, value]) => key !== "visible" && value !== ""),
  );
}

export function visibleGroupsForCondition(
  story: Story,
  condition: Condition,
  furthestReadIndex: number,
) {
  if (condition !== "H") return [];
  return story.groups.filter((group) => group.revealAt <= furthestReadIndex);
}

export function canFinishFirstHalf(state: ExperimentState, story: Story) {
  return state.furthestReadIndex >= story.interruptionIndex;
}

export function canFinishStory(state: ExperimentState, story: Story) {
  return state.furthestReadIndex >= story.blocks.length - 1;
}
